import React from 'react';
import { Link } from 'react-router-dom';
import { IconCode, IconPlus, IconLightning, IconTerminal } from '../components/Icons';
import './Bots.css';

const BOTS = [
  {
    slug: 'code-assist',
    Icon: IconCode,
    name: 'Code Assist',
    tagline: 'Your Discord coding companion.',
    desc: 'Write, debug, explain, and optimise code directly from Discord. Supports 20+ languages with instant responses via slash commands.',
    status: 'live',
    tags: ['Coding', 'Debugging', 'Developer Tools'],
  },
];

const STATUS_LABELS = {
  live:          { label: 'Live',        cls: 'badge--green' },
  'coming-soon': { label: 'Coming Soon', cls: 'badge--amber' },
  beta:          { label: 'Beta',        cls: 'badge--blue'  },
};

export default function Bots() {
  return (
    <div className="page bots-page">
      <section className="bots-hero">
        <div className="bots-hero__blob" aria-hidden="true" />
        <div className="container animate-fade-up">
          <span className="eyebrow">Our Products</span>
          <h1 className="display-title" style={{ maxWidth: 620 }}>
            All PERM Systems<br />
            <span style={{ color: 'var(--blue-dark)' }}>products.</span>
          </h1>
          <p className="section-body">
            Explore our growing library of Discord products. Each one is crafted for
            a specific purpose and built to run reliably at scale.
          </p>
        </div>
      </section>

      {/* ── Live bots grid ── */}
      <section className="section">
        <div className="container">
          <div className="bots-grid">
            {BOTS.map(({ slug, Icon, name, tagline, desc, status, tags }, i) => {
              const s = STATUS_LABELS[status] || STATUS_LABELS['coming-soon'];
              return (
                <div key={slug} className={`bots-card animate-fade-up delay-${i + 1}`}>
                  <div className="bots-card__top">
                    <div className="bots-card__icon-wrap">
                      <Icon size={24} color="var(--blue-dark)" />
                    </div>
                    <span className={`badge ${s.cls}`}>{s.label}</span>
                  </div>
                  <h2 className="bots-card__name">{name}</h2>
                  <p className="bots-card__tagline">{tagline}</p>
                  <p className="bots-card__desc">{desc}</p>
                  <div className="bots-card__tags">
                    {tags.map(t => <span key={t} className="bots-card__tag">{t}</span>)}
                  </div>
                  <div className="bots-card__actions">
                    <Link to={`/${slug}/home`} className="btn btn--primary">View Bot</Link>
                    <Link to={`/${slug}/invite`} className="btn btn--outline">Invite</Link>
                  </div>
                </div>
              );
            })}

            <div className="bots-card bots-card--placeholder animate-fade-up delay-2">
              <div className="bots-card__placeholder-icon">
                <IconPlus size={22} color="var(--gray-lt)" />
              </div>
              <h3>More Coming Soon</h3>
              <p>We are building new products. Join our Discord to be notified first.</p>
              <a href="https://discord.gg/permsystems" target="_blank" rel="noopener noreferrer"
                 className="btn btn--outline btn--sm" style={{ marginTop: 16 }}>
                Join Discord
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Code Assist Desktop leak ── */}
      <section className="section bots-leak-section">
        <div className="container">
          <div className="bots-leak animate-fade-up">

            {/* Header band */}
            <div className="bots-leak__header">
              <div className="bots-leak__pulse-wrap" aria-hidden="true">
                <span className="bots-leak__pulse-ring" />
                <span className="bots-leak__pulse-dot" />
              </div>
              <span className="bots-leak__eyebrow">Coming Soon</span>
            </div>

            {/* Body */}
            <div className="bots-leak__body">
              <div className="bots-leak__left">
                <h2 className="bots-leak__title">
                  Code Assist<br />
                  <span className="bots-leak__title-accent">Desktop App</span>
                </h2>
                <p className="bots-leak__desc">
                  Code Assist is leaving Discord and coming to your desktop. The same
                  custom PERM AI that powers the bot — now embedded directly into your
                  development workflow. As you write code, it analyses in real time:
                  catching bugs before you run, suggesting improvements inline, and
                  explaining logic as you type.
                </p>
                <p className="bots-leak__desc" style={{ marginTop: 12 }}>
                  No copy-pasting into Discord. No switching context. Just intelligent
                  coding assistance, right where your code lives.
                </p>

                <div className="bots-leak__features">
                  {[
                    { Icon: IconLightning, text: 'Real-time code analysis as you type' },
                    { Icon: IconTerminal,  text: 'Inline bug detection and fix suggestions' },
                    { Icon: IconCode,      text: 'Powered by PERM Systems\' custom AI model' },
                  ].map(({ Icon, text }) => (
                    <div key={text} className="bots-leak__feature">
                      <div className="bots-leak__feature-icon">
                        <Icon size={14} color="var(--blue-dark)" />
                      </div>
                      <span>{text}</span>
                    </div>
                  ))}
                </div>

                <div className="bots-leak__actions">
                  <a href="https://discord.gg/permsystems" target="_blank" rel="noopener noreferrer"
                     className="btn btn--primary">
                    Get notified on Discord
                  </a>
                  <Link to="/code-assist/about" className="btn btn--outline">
                    Learn about Code Assist
                  </Link>
                </div>
              </div>

              {/* Fake editor preview */}
              <div className="bots-leak__right" aria-hidden="true">
                <div className="bots-leak__editor">
                  <div className="bots-leak__editor-bar">
                    <span className="bots-leak__editor-dot" style={{ background: '#ff5f57' }} />
                    <span className="bots-leak__editor-dot" style={{ background: '#febc2e' }} />
                    <span className="bots-leak__editor-dot" style={{ background: '#28c840' }} />
                    <span className="bots-leak__editor-title">main.py — Code Assist Desktop</span>
                  </div>
                  <div className="bots-leak__editor-body">
                    <div className="bots-leak__code-line">
                      <span className="ble-num">1</span>
                      <span><span className="tok tok--kw">def </span><span className="tok tok--fn">calculate</span><span className="tok tok--pl">(a, b):</span></span>
                    </div>
                    <div className="bots-leak__code-line">
                      <span className="ble-num">2</span>
                      <span><span className="tok tok--pl">    result = a </span><span className="tok tok--kw">/ </span><span className="tok tok--pl">b</span></span>
                    </div>
                    <div className="bots-leak__code-line bots-leak__code-line--error">
                      <span className="ble-num">2</span>
                      <span><span className="tok tok--pl">    result = a </span><span className="tok tok--kw">/ </span><span className="tok tok--pl">b</span></span>
                      <div className="bots-leak__inline-hint">
                        <div className="bots-leak__inline-hint-dot" />
                        ZeroDivisionError if b is 0
                      </div>
                    </div>
                    <div className="bots-leak__code-line">
                      <span className="ble-num">3</span>
                      <span><span className="tok tok--kw">    return </span><span className="tok tok--pl">result</span></span>
                    </div>
                    <div className="bots-leak__code-line" style={{ opacity: 0.35 }}>
                      <span className="ble-num">4</span>
                      <span />
                    </div>
                    <div className="bots-leak__code-line">
                      <span className="ble-num">5</span>
                      <span><span className="tok tok--fn">calculate</span><span className="tok tok--pl">(10, 0)</span></span>
                    </div>
                  </div>
                  <div className="bots-leak__editor-suggestion">
                    <div className="bots-leak__suggestion-header">
                      <span className="bots-leak__suggestion-dot" />
                      Code Assist suggestion
                    </div>
                    <div className="bots-leak__suggestion-body">
                      Add a zero-check before dividing:<br />
                      <code>if b == 0: return None</code>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
}